import { formatBudgetDateOnly } from '../../../pdf/pages/budget-preview/budget-preview.component';
import { SaleOrderPdfVersion } from '../../../../shared/models/sale-order-pdf-version';

const FALLBACK_FILE_BASE_NAME = 'presupuesto';
const HAS_TIMEZONE_DESIGNATOR = /(Z|[+-]\d{2}:?\d{2})$/i;
const INVALID_FILE_NAME_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g;

const pad = (value: number): string => value.toString().padStart(2, '0');

export function formatVersionLabel(versionNumber: number): string {
  return `v${versionNumber}`;
}

/**
 * Issue date as `dd/MM/yyyy HH:mm` in the browser's local time. The API serialises UTC instants
 * without an offset, so a timestamp lacking one is read as UTC.
 */
export function formatVersionIssuedOn(value: string | null | undefined): string {
  if (!value) return '';

  const normalized = HAS_TIMEZONE_DESIGNATOR.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Expiration is a calendar date: same rule as the budget page, no timezone shift. */
export function formatVersionExpiration(value: string | null | undefined): string {
  return formatBudgetDateOnly(value);
}

export function buildVersionFileName(budgetName: string | null | undefined, versionNumber: number): string {
  const base = (budgetName ?? '')
    .replace(INVALID_FILE_NAME_CHARS, '')
    .trim()
    .replace(/\s+/g, '_');

  return `${base || FALLBACK_FILE_BASE_NAME}_${formatVersionLabel(versionNumber)}.pdf`;
}

export function hasNoVersions(versions: readonly SaleOrderPdfVersion[]): boolean {
  return versions.length === 0;
}

export interface ArchiveVersionConfirmation {
  title: string;
  content: string;
  okText: string;
  cancelText: string;
}

/** Texts of the confirmation shown before archiving ("Eliminar") a version from the list. */
export function buildArchiveVersionConfirmation(versionNumber: number): ArchiveVersionConfirmation {
  return {
    title: `¿Quieres eliminar la versión ${versionNumber}?`,
    content: 'Ya no se mostrará en la lista de versiones.',
    okText: 'Eliminar',
    cancelText: 'Cancelar',
  };
}

export function formatVersionArchivedMessage(versionNumber: number): string {
  return `La v${versionNumber} ha sido archivada.`;
}

const NO_VALUE = '—';

/** Issue currency of a converted version; legacy rows show the budget ISO, or a dash when unknown. */
export function formatVersionCurrency(
  version: Pick<SaleOrderPdfVersion, 'toCurrencyCode'>,
  budgetCurrencyCode: string | null | undefined
): string {
  return version.toCurrencyCode || budgetCurrencyCode || NO_VALUE;
}

/** Display rate with up to 8 decimals (no trailing zeros), or a dash for non-converted versions. */
export function formatVersionRate(rate: number | null | undefined): string {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return NO_VALUE;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 8,
    useGrouping: false,
  })
    .format(rate)
    .replace(/\.0$/, '');
}

/** Conversion date is a calendar date (`dd/MM/yyyy`); a dash for non-converted versions. */
export function formatVersionConversionDate(value: string | null | undefined): string {
  return formatBudgetDateOnly(value) || NO_VALUE;
}
